import { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import './../../assets/css/UC/LanguageSwitcher.scss';

const LanguageSwitcher = ({ 
  showLabels = false, 
  showFlags = false, 
  compact = false,
  className = '' 
}) => {
  const { 
    currentLanguage, 
    availableLanguages, 
    changeLanguage, 
    isTranslating,
    language 
  } = useTranslation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLanguageChange = (langCode) => {
    if (langCode !== currentLanguage && !isTranslating) {
      changeLanguage(langCode);
      setIsDropdownOpen(false); // Close dropdown after selection
    }
  };

  const toggleDropdown = () => {
    if (!isTranslating) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Flag emoji mapping
  const flagEmojis = {
    en: 'en',
    fr: '🇫🇷'
  };

  if (compact) {
    return (
      <div className={`language-switcher compact ${className}`}>
        <select 
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          disabled={isTranslating}
          aria-label={language.languageSwitcher?.selectLanguage || "Select Language"}
        >
          {Object.entries(availableLanguages).map(([code, { name }]) => (
            <option key={code} value={code}>
              {showFlags && flagEmojis[code]} {showLabels ? name : code.toUpperCase()}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={`language-switcher dropdown ${className} ${isDropdownOpen ? 'open' : ''}`} ref={dropdownRef}>
      <div 
        className={`language-trigger ${isTranslating ? 'disabled' : ''}`}
        onClick={toggleDropdown}
        role="button"
        tabIndex={0}
        aria-label={language.languageSwitcher?.selectLanguage || "Select Language"}
        aria-expanded={isDropdownOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown();
          }
        }}
      >
        <i className="fa fa-globe" aria-hidden="true"></i>
        <span className="language-code">{currentLanguage.toUpperCase()}</span>
      </div>

      {isDropdownOpen && (
        <div className="language-dropdown">
          {Object.entries(availableLanguages).map(([code, { name }]) => (
            <button
              key={code}
              className={`language-option ${currentLanguage === code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(code)}
              disabled={isTranslating}
              aria-label={language.languageSwitcher?.switchTo?.replace('{name}', name) || `Switch to ${name}`}
            >
              {showFlags && (
                <span className="flag-emoji" role="img" aria-label={`${name} flag`}>
                  {flagEmojis[code]}
                </span>
              )}
              <span className="language-name">{name}</span>
              {currentLanguage === code && (
                <i className="fa fa-check" aria-hidden="true"></i>
              )}
            </button>
          ))}
        </div>
      )}
      
      {isTranslating && (
        <div className="loading-indicator" title={language.languageSwitcher?.loadingTranslations || "Loading translations..."}>
          <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
